import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProductById, type ProductDetail } from '@/lib/queries/products'
import { getAllStockBundles } from '@/lib/queries/bundles'
import { getEtsyConfigSets, type EtsyConfigSetListItem } from '@/lib/queries/configSets'
import { productService } from '@/services/productService'
import { uploadHeroImage } from '@/lib/media'

// Minimal portable-text -> plain text
const portableTextToPlain = (desc: any): string => {
  if (!desc) return ''
  if (typeof desc === 'string') return desc
  if (Array.isArray(desc)) {
    return desc
      .map((block: any) => {
        if (block?._type === 'block' && Array.isArray(block.children)) {
          return block.children.map((c: any) => c?.text ?? '').join('')
        }
        return ''
      })
      .join('\n')
  }
  return ''
}

type ProductKind = 'physical' | 'print_on_demand'

function EditProductForm({ product, id }: { product: ProductDetail; id: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(product.title || '')
  const [price, setPrice] = useState<string>(String(product.rrp ?? ''))
  const [cost, setCost] = useState<string>(product.cost != null ? String(product.cost) : '')
  const [productType, setProductType] = useState<ProductKind>(
    product.isPrintOnDemand ? 'print_on_demand' : 'physical'
  )
  const [bundleSku, setBundleSku] = useState<string | null>(
    product.relations?.relatedBundle?.sku ?? null
  )
  const [heroAssetId, setHeroAssetId] = useState<string | null>(
    product.media?.hero?.asset?._ref ?? null
  )
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(
    product.media?.hero?.url ?? null
  )
  const [heroError, setHeroError] = useState<string | null>(null)
  const [isUploadingHero, setIsUploadingHero] = useState(false)
  const [podAssetId, setPodAssetId] = useState<string | null>(
    product.media?.pod?.asset?._ref ?? null
  )
  const [podPreviewUrl, setPodPreviewUrl] = useState<string | null>(
    product.media?.pod?.url ?? null
  )
  const [podError, setPodError] = useState<string | null>(null)
  const [isUploadingPod, setIsUploadingPod] = useState(false)
  const [sku, setSku] = useState<string>(product.sku || '')
  const [description, setDescription] = useState<string>(portableTextToPlain(product.description))

  // Existing Etsy config mapping on the product
  const existingEtsyMapping = (product.channelMapping || []).find((m) => m.channel === 'etsy')
  const [etsyConfigSetId, setEtsyConfigSetId] = useState<string | null>(
    existingEtsyMapping?.configSet?._ref ?? null
  )

  const {
    data: bundles = [],
    isLoading: isLoadingBundles,
  } = useQuery({
    queryKey: ['stockBundles', 'all'],
    queryFn: getAllStockBundles,
  })

  const {
    data: etsyConfigSets = [],
    isLoading: isLoadingEtsyConfigSets,
  } = useQuery<EtsyConfigSetListItem[]>({
    queryKey: ['etsyConfigSets'],
    queryFn: () => getEtsyConfigSets(),
    staleTime: 0,
    gcTime: 0,
  })

  const { mutateAsync: doUpdate, isPending } = useMutation({
    mutationFn: (updates: {
      title?: string
      rrp?: number
      description?: any[]
      sku?: string
      cost?: number
      isPrintOnDemand?: boolean
      bundleSku?: string | null
    }) => {
      const enriched: any = {
        ...updates,
        // Keep normalized fields in sync with Sanity-aligned ones
        name: updates.title ?? product.title,
        basePrice: updates.rrp ?? product.rrp,
        // Only persist cost if provided; mainly relevant for POD products
        ...(typeof updates.cost === 'number' ? { cost: updates.cost } : {}),
      }
      return productService.updateProduct(id, enriched)
    },
  })

  const onSave = async () => {
    // Always persist exactly what the user typed (trimmed),
    // falling back to the existing title if the field is left blank.
    const normalizedTitle = title.trim() || product.title || ''

    const numericPrice = price ? parseFloat(price) : undefined
    const numericCost = cost ? parseFloat(cost) : undefined

    // Resolve bundle reference for physical products so Sanity stores a real reference
    let relationsUpdate: ProductDetail['relations'] | undefined
    if (productType === 'physical') {
      if (bundleSku) {
        // Re-use already-fetched stock bundles to resolve the reference
        const bundle: any = (bundles as any[]).find((b) => b.sku === bundleSku)
        if (bundle && bundle._id) {
          // Sanity reference: {_type: 'reference', _ref: bundleId}
          relationsUpdate = {
            ...(product.relations || {}),
            relatedBundle: {
              _type: 'reference',
              _ref: bundle._id,
            } as any,
          }
        }
      } else {
        // Explicitly clear related bundle when selecting "No bundle"
        relationsUpdate = {
          ...(product.relations || {}),
          relatedBundle: null as any,
        }
      }
    } else {
      // POD products should not keep a related bundle
      relationsUpdate = {
        ...(product.relations || {}),
        relatedBundle: null as any,
      }
    }

    // Map plain text back to minimal portable text for Sanity description
    const portableDescription =
      description.trim().length > 0
        ? [
            {
              _type: 'block',
              style: 'normal',
              markDefs: [],
              children: [
                {
                  _type: 'span',
                  text: description.trim(),
                  marks: [],
                },
              ],
            },
          ]
        : []

    // Build updated channel mapping for Etsy config set
    const currentMappings = Array.isArray(product.channelMapping) ? product.channelMapping : []
    const nonEtsyMappings = currentMappings.filter((m) => m.channel !== 'etsy')
    let nextChannelMapping = nonEtsyMappings
    if (etsyConfigSetId) {
      const existing = existingEtsyMapping
      const baseStatus = existing?.status || 'not_synced'
      nextChannelMapping = [
        ...nonEtsyMappings,
        {
          channel: 'etsy',
          status: baseStatus,
          // For Sanity references, _type is inferred; we only need _ref here.
          configSet: { _ref: etsyConfigSetId },
        },
      ]
    }

    const mediaUpdate: any = {}
    if (heroAssetId != null || product.media?.hero) {
      mediaUpdate.media = {
        ...(product.media || {}),
        hero:
          heroAssetId != null
            ? {
                _type: 'image',
                asset: {
                  _type: 'reference',
                  _ref: heroAssetId,
                },
              }
            : null,
      }
    }
    if (podAssetId != null || product.media?.pod) {
      mediaUpdate.media = {
        ...(mediaUpdate.media || product.media || {}),
        pod:
          podAssetId != null
            ? {
                _type: 'image',
                asset: {
                  _type: 'reference',
                  _ref: podAssetId,
                },
              }
            : null,
      }
    }

    await doUpdate({
      title: normalizedTitle,
      rrp: numericPrice,
      description: portableDescription,
      sku,
      // Persist product type via isPrintOnDemand flag
      isPrintOnDemand: productType === 'print_on_demand',
      // For POD products, allow updating cost as well
      ...(productType === 'print_on_demand' ? { cost: numericCost } : {}),
      // For physical products, allow assigning a bundle by SKU
      bundleSku: productType === 'physical' ? bundleSku : null,
      ...(relationsUpdate ? { relations: relationsUpdate } : {}),
      channelMapping: nextChannelMapping,
      ...(Object.keys(mediaUpdate).length > 0 ? mediaUpdate : {}),
    })

    // Update cached product detail for snappy UX
    queryClient.setQueryData(['product', id], (prev: any) => {
      if (!prev) return prev
      const nextTypeIsPod = productType === 'print_on_demand'
      return {
        ...prev,
        title: normalizedTitle || prev.title,
        rrp: numericPrice ?? prev.rrp,
        sku: sku || prev.sku,
        description: portableDescription,
        isPrintOnDemand: nextTypeIsPod,
        cost: nextTypeIsPod ? numericCost ?? prev.cost : undefined,
        relations: {
          ...(prev.relations || {}),
          relatedBundle:
            productType === 'physical' && (bundleSku || prev.relations?.relatedBundle?.sku)
              ? {
                  ...(prev.relations?.relatedBundle || {}),
                  sku: bundleSku || prev.relations?.relatedBundle?.sku,
                }
              : undefined,
        },
        media:
          heroAssetId != null ||
          podAssetId != null ||
          prev.media?.hero ||
          prev.media?.pod
            ? {
                ...(prev.media || {}),
                hero:
                  heroAssetId != null
                    ? {
                        ...(prev.media?.hero || {}),
                        asset: {
                          _ref: heroAssetId,
                        },
                      }
                    : prev.media?.hero,
                pod:
                  podAssetId != null
                    ? {
                        ...(prev.media?.pod || {}),
                        asset: {
                          _ref: podAssetId,
                        },
                      }
                    : prev.media?.pod,
              }
            : prev.media,
      }
    })
    await queryClient.invalidateQueries({ queryKey: ['product', id] })
    await queryClient.invalidateQueries({ queryKey: ['products'] })
    navigate(`/products/${id}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900">Edit Product</h1>
        </div>
      </header>

      <main className="p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Product Type</h2>
          <div className="space-y-2">
            <button
              type="button"
              className={`w-full text-left border rounded-lg p-3 ${
                productType === 'physical' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
              }`}
              aria-pressed={productType === 'physical'}
              onClick={() => setProductType('physical')}
            >
              <div className="font-medium">Physical Product</div>
              <div className="text-xs text-gray-600 mt-1">Requires a bundle and uses RRP</div>
            </button>
            <button
              type="button"
              className={`w-full text-left border rounded-lg p-3 ${
                productType === 'print_on_demand'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 bg-white'
              }`}
              aria-pressed={productType === 'print_on_demand'}
              onClick={() => setProductType('print_on_demand')}
            >
              <div className="font-medium">Print on Demand</div>
              <div className="text-xs text-gray-600 mt-1">Uses supplier cost and base price</div>
            </button>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-700 mb-1">Channel Config (Etsy)</h2>
          {isLoadingEtsyConfigSets ? (
            <div className="text-sm text-gray-500">Loading Etsy config sets...</div>
          ) : etsyConfigSets.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700">
              No Etsy config sets found. Create them in Sanity Studio and they will appear here.
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-xs text-gray-600" htmlFor="etsy-config-select">
                Etsy Config Set
              </label>
              <select
                id="etsy-config-select"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
                value={etsyConfigSetId || ''}
                onChange={(e) => setEtsyConfigSetId(e.target.value || null)}
              >
                <option value="">No Etsy config set</option>
                {etsyConfigSets.map((cfg) => (
                  <option key={cfg._id} value={cfg._id}>
                    {cfg.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {productType === 'physical' && (
          <div>
            <h2 className="text-sm font-medium text-gray-700 mb-1">Bundle</h2>
            {isLoadingBundles ? (
              <div className="text-sm text-gray-500">Loading bundles...</div>
            ) : (bundles as any[]).length === 0 ? (
              <div className="bg-amber-50 border border-amber-400 rounded-lg p-3">
                <h3 className="text-amber-800 font-semibold mb-1">No Bundles Found</h3>
                <p className="text-xs text-amber-800">
                  You need to create a bundle first before assigning one to this product.
                </p>
                <a
                  href="/bundles"
                  className="inline-block mt-2 bg-blue-600 text-white px-3 py-2 rounded text-xs font-medium"
                >
                  Create Bundle
                </a>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-xs text-gray-600" htmlFor="bundle-select">
                  Select Bundle
                </label>
                <select
                  id="bundle-select"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
                  value={bundleSku || ''}
                  onChange={(e) => setBundleSku(e.target.value || null)}
                >
                  <option value="">No bundle</option>
                  {(bundles as any[]).map((b) => (
                    <option key={b._id} value={b.sku}>
                      {b.sku} - {b.title || b.name || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sku">
            SKU
          </label>
          <input
            id="sku"
            aria-label="SKU"
            type="text"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder={product.sku || 'PROD-001'}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="title">
            Title
          </label>
          <input
            id="title"
            aria-label="Title"
            type="text"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={product.title || 'Untitled Product'}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image</label>
          {heroPreviewUrl && (
            <div className="mb-2 relative inline-block">
              <img
                src={heroPreviewUrl}
                alt="Hero preview"
                className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
              />
              <button
                type="button"
                aria-label="Remove hero image"
                className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-white/90 text-red-600 text-xs font-bold flex items-center justify-center shadow-sm border border-red-200"
                onClick={() => {
                  setHeroAssetId(null)
                  setHeroPreviewUrl(null)
                  setHeroError(null)
                }}
              >
                ×
              </button>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (!file) return
              setHeroError(null)
              if (!file.type.startsWith('image/')) {
                setHeroError('Please select an image file.')
                return
              }
              try {
                setIsUploadingHero(true)
                const { assetId, url } = await uploadHeroImage(file)
                setHeroAssetId(assetId)
                setHeroPreviewUrl(url ?? null)
              } catch (err: any) {
                setHeroError(err?.message || 'Failed to upload image.')
              } finally {
                setIsUploadingHero(false)
              }
            }}
            className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {heroError && <p className="mt-1 text-sm text-red-600">{heroError}</p>}
          {isUploadingHero && <p className="mt-1 text-xs text-gray-500">Uploading image…</p>}
        </div>

        {productType === 'print_on_demand' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">POD Hero Image</label>
            {podPreviewUrl && (
              <div className="mb-2 relative inline-block">
                <img
                  src={podPreviewUrl}
                  alt="POD hero preview"
                  className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  aria-label="Remove POD hero image"
                  className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-white/90 text-red-600 text-xs font-bold flex items-center justify-center shadow-sm border border-red-200"
                  onClick={() => {
                    setPodAssetId(null)
                    setPodPreviewUrl(null)
                    setPodError(null)
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setPodError(null)
                if (!file.type.startsWith('image/')) {
                  setPodError('Please select an image file.')
                  return
                }
                try {
                  setIsUploadingPod(true)
                  const { assetId, url } = await uploadHeroImage(file)
                  setPodAssetId(assetId)
                  setPodPreviewUrl(url ?? null)
                } catch (err: any) {
                  setPodError(err?.message || 'Failed to upload image.')
                } finally {
                  setIsUploadingPod(false)
                }
              }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {podError && <p className="mt-1 text-sm text-red-600">{podError}</p>}
            {isUploadingPod && <p className="mt-1 text-xs text-gray-500">Uploading POD image…</p>}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="price">
            {productType === 'print_on_demand' ? 'Base Price' : 'RRP'}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-lg">£</span>
            <input
              id="price"
              aria-label={productType === 'print_on_demand' ? 'Base Price' : 'RRP'}
              type="number"
              step="0.01"
              className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={String(product.rrp ?? '')}
            />
          </div>
        </div>

        {productType === 'print_on_demand' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cost">
              Cost
            </label>
            <div className="flex items-center gap-2">
              <span className="text-lg">£</span>
              <input
                id="cost"
                aria-label="Cost"
                type="number"
                step="0.01"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder={product.cost != null ? String(product.cost) : '0.00'}
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this product..."
          />
        </div>
      </main>

      <footer className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
        <button
          className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
          onClick={() => navigate(`/products/${id}`)}
        >
          Cancel
        </button>
        <button
          className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
          onClick={onSave}
          disabled={isPending}
        >
          Save
        </button>
      </footer>
    </div>
  )
}

export default function EditProduct() {
  const { id } = useParams<{ id: string }>()
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProductById(id!),
    enabled: !!id,
  })

  if (isLoading || !product) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading product...</div>
      </div>
    )
  }

  return <EditProductForm product={product} id={id!} />
}

